import { useCallback, useRef } from 'react';
import { FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import { getCurrencyDivisor } from 'utilities/formatNumbers';
import { UseWaterfallInputHandlersParams, UseWaterfallInputHandlersResult } from './useWaterfallInputHandlers.props';
import {
  CAP_TABLE_ALIAS,
  COMPANY_ALIAS,
  EXIT_DATE_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FOREIGN_EXCHANGE_RATE_BASE_ALIAS,
  FOREIGN_EXCHANGE_RATE_TARGET_ALIAS,
  FOREIGN_LESS_DEBT_ALIAS,
  FOREIGN_PLUS_CASH_ALIAS,
  LESS_DEBT_ALIAS,
  MEASUREMENT_DATE_ALIAS,
  PLUS_CASH_ALIAS,
} from '../../SpreadsheetConfig/constants';

export type {
  UseWaterfallInputHandlersParams,
  UseWaterfallInputHandlersResult,
} from './useWaterfallInputHandlers.props';

const SCALABLE_INPUT_ALIASES = [
  EXIT_EQUITY_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  FUND_EXIT_EQUITY_ALIAS,
  FUND_EXIT_ENTERPRISE_ALIAS,
  PLUS_CASH_ALIAS,
  LESS_DEBT_ALIAS,
  FOREIGN_PLUS_CASH_ALIAS,
  FOREIGN_LESS_DEBT_ALIAS,
];

export const useWaterfallInputHandlers = ({
  urlState,
  onChange,
  setScenarios,
  resetChildrenSheets,
  capTableList,
  childrenCapTables,
  companyMeasurementDates,
  exchangeRate,
  units,
}: UseWaterfallInputHandlersParams): UseWaterfallInputHandlersResult => {
  // Refs to avoid stale closure issues in handlers passed to spreadsheet components
  const companyMeasurementDatesRef = useRef(companyMeasurementDates);
  const capTableListRef = useRef(capTableList);
  const childrenCapTablesRef = useRef(childrenCapTables);

  // Keep refs updated
  companyMeasurementDatesRef.current = companyMeasurementDates;
  capTableListRef.current = capTableList;
  childrenCapTablesRef.current = childrenCapTables;

  // ============================================================
  // Individual Handlers
  // ============================================================
  const handleExitDateChange = useCallback(
    (value: unknown) => {
      urlState.setExitDate(value as string);
    },
    [urlState]
  );

  const handleEquityValueChange = useCallback(
    (value: unknown) => {
      urlState.setEquityValue(value as number);
    },
    [urlState]
  );

  const handleEnterpriseValueChange = useCallback(
    (value: unknown) => {
      urlState.setEnterpriseValue(value as number);
    },
    [urlState]
  );

  const handleCashChange = useCallback(
    (value: unknown) => {
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      urlState.setCash(num);
    },
    [urlState]
  );

  const handleDebtChange = useCallback(
    (value: unknown) => {
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      urlState.setDebt(num);
    },
    [urlState]
  );

  const handleCapTableChange = useCallback(
    (value: unknown) => {
      // Use refs to avoid stale closure - spreadsheet components may cache the callback
      const currentCapTableList = capTableListRef.current;
      const currentChildrenCapTables = childrenCapTablesRef.current;
      // Use toString comparison to handle both string and number IDs
      const valueStr = String(value);
      // Look in both main cap table list and children cap tables
      const captable =
        currentCapTableList.find(ct => String(ct.id) === valueStr) ||
        currentChildrenCapTables.find(ct => String(ct.id) === valueStr);
      if (captable) {
        urlState.setCaptable(captable);
        setScenarios(undefined);
        resetChildrenSheets();
      }
    },
    [urlState, setScenarios, resetChildrenSheets]
  );

  const handleCompanyChange = useCallback(
    (value: unknown) => {
      const newCompanyId = Number(value);
      urlState.resetForCompanyChange(newCompanyId);
      setScenarios(undefined);
      resetChildrenSheets();
    },
    [urlState, setScenarios, resetChildrenSheets]
  );

  const handleMeasurementDateChange = useCallback(
    (value: unknown) => {
      // Use ref to avoid stale closure - spreadsheet components may cache the callback
      const currentMeasurementDates = companyMeasurementDatesRef.current;
      // Use toString comparison to handle both string and number IDs
      const valueStr = String(value);
      const newMeasurementDate = currentMeasurementDates.find(md => String(md.id) === valueStr);
      if (newMeasurementDate) {
        // Update both params atomically so the new date cannot be overwritten by a second navigation.
        urlState.setMeasurementDate(newMeasurementDate, { clearCaptable: true });
        setScenarios(undefined);
      }
    },
    [urlState, setScenarios]
  );

  // ============================================================
  // Foreign-currency handlers persist canonical values in the Cap Table currency.
  // ============================================================
  const convertForeignValueToBase = useCallback(
    (value: unknown) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && Number.isFinite(exchangeRate) && exchangeRate != null && exchangeRate > 0
        ? numericValue / exchangeRate
        : null;
    },
    [exchangeRate]
  );

  const scaleInputValue = useCallback(
    (alias: string | undefined, value: unknown) => {
      if (!alias || !SCALABLE_INPUT_ALIASES.includes(alias) || value === '' || value === null || value === undefined) {
        return value;
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue * getCurrencyDivisor(units) : value;
    },
    [units]
  );

  // ============================================================
  // Main Workbook Change Handler
  // ============================================================
  const onWorkbookChange = useCallback(
    (cell: EvaluatedCell, value: unknown) => {
      const numericInput = Number(value);
      const isExchangeRateInput = [FOREIGN_EXCHANGE_RATE_BASE_ALIAS, FOREIGN_EXCHANGE_RATE_TARGET_ALIAS].includes(
        cell.alias ?? ''
      );
      if (isExchangeRateInput && (!Number.isFinite(numericInput) || numericInput <= 0)) {
        return;
      }

      const isForeignMonetaryInput = [
        FUND_EXIT_ENTERPRISE_ALIAS,
        FUND_EXIT_EQUITY_ALIAS,
        FOREIGN_PLUS_CASH_ALIAS,
        FOREIGN_LESS_DEBT_ALIAS,
      ].includes(cell.alias ?? '');
      if (
        isForeignMonetaryInput &&
        (!Number.isFinite(numericInput) || !Number.isFinite(exchangeRate) || exchangeRate == null || exchangeRate <= 0)
      ) {
        return;
      }

      const scaledValue = scaleInputValue(cell.alias, value);
      onChange(cell, scaledValue);
      switch (cell.alias) {
        case EXIT_DATE_ALIAS:
          handleExitDateChange(value);
          break;
        case EXIT_EQUITY_ALIAS:
          handleEquityValueChange(scaledValue);
          break;
        case FUND_EXIT_ENTERPRISE_ALIAS:
          handleEnterpriseValueChange(convertForeignValueToBase(scaledValue));
          break;
        case FUND_EXIT_EQUITY_ALIAS:
          handleEquityValueChange(convertForeignValueToBase(scaledValue));
          break;
        case EXIT_ENTERPRISE_VALUE_ALIAS:
          handleEnterpriseValueChange(scaledValue);
          break;
        case PLUS_CASH_ALIAS:
          handleCashChange(scaledValue);
          break;
        case LESS_DEBT_ALIAS:
          handleDebtChange(scaledValue);
          break;
        case FOREIGN_PLUS_CASH_ALIAS:
          handleCashChange(convertForeignValueToBase(scaledValue));
          break;
        case FOREIGN_LESS_DEBT_ALIAS:
          handleDebtChange(convertForeignValueToBase(scaledValue));
          break;
        case FOREIGN_EXCHANGE_RATE_BASE_ALIAS:
          urlState.setForeignExchangeRate(1 / numericInput, true, 'base');
          break;
        case FOREIGN_EXCHANGE_RATE_TARGET_ALIAS:
          urlState.setForeignExchangeRate(numericInput, true, 'foreign');
          break;
        case CAP_TABLE_ALIAS:
          handleCapTableChange(value);
          break;
        case COMPANY_ALIAS:
          handleCompanyChange(value);
          break;
        case MEASUREMENT_DATE_ALIAS:
          handleMeasurementDateChange(value);
          break;
        default:
          break;
      }
    },
    [
      onChange,
      handleExitDateChange,
      handleEquityValueChange,
      handleEnterpriseValueChange,
      handleCapTableChange,
      handleCompanyChange,
      handleMeasurementDateChange,
      handleCashChange,
      handleDebtChange,
      exchangeRate,
      convertForeignValueToBase,
      scaleInputValue,
      urlState,
    ]
  );

  return {
    onWorkbookChange,
    handleExitDateChange,
    handleEquityValueChange,
    handleEnterpriseValueChange,
    handleCashChange,
    handleDebtChange,
    handleCapTableChange,
    handleCompanyChange,
    handleMeasurementDateChange,
  };
};
